import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import ContactMailOutlinedIcon from "@mui/icons-material/ContactMailOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { updateTenantContact, getTenant, syncTenantToHubSpot } from "../../common/tenantApi";
import { readLandingLead } from "../../common/landingLead";
import { trackEvent } from "../../hooks/useTrackEvent";

function prefillEmail() {
  const lead = readLandingLead();
  if (lead.email) return lead.email;
  try {
    const profile = JSON.parse(sessionStorage.getItem("onboarding_profile") || "{}");
    return profile.email || "";
  } catch {
    return "";
  }
}

function prefillContactName() {
  const lead = readLandingLead();
  if (lead.nombre?.trim()) return lead.nombre.trim();
  try {
    const profile = JSON.parse(sessionStorage.getItem("onboarding_profile") || "{}");
    const parts = [profile.nombre, profile.apellido].filter(Boolean);
    return parts.join(" ").trim();
  } catch {
    return "";
  }
}

export default function StepContacto() {
  const navigate = useNavigate();
  const tenantId = sessionStorage.getItem("onboarding_tenant_id");
  const [showSocial, setShowSocial] = useState(false);
  const [form, setForm] = useState({
    name: prefillContactName(),
    email: prefillEmail(),
    phone: readLandingLead().whatsapp || "",
    whatsapp: true,
    linkedin: "",
    instagram: "",
    website: "",
    other_label: "",
    other_value: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!tenantId) {
      navigate("/onboarding/workspace", { replace: true });
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const tenant = await getTenant();
        if (cancelled || !tenant?.contact) return;
        const social = tenant.contact.social || {};
        setForm((prev) => ({
          ...prev,
          name: tenant.contact.name || prev.name,
          email: tenant.contact.email || prev.email,
          phone: tenant.contact.phone || prev.phone,
          whatsapp: tenant.contact.whatsapp ?? prev.whatsapp,
          linkedin: social.linkedin || "",
          instagram: social.instagram || "",
          website: social.website || "",
          other_label: social.other_label || "",
          other_value: social.other_value || "",
        }));
      } catch {
        /* ignore — usuario puede completar manualmente */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tenantId, navigate]);

  const socialFilled = useMemo(
    () =>
      Boolean(
        form.linkedin.trim() ||
          form.instagram.trim() ||
          form.website.trim() ||
          (form.other_label.trim() && form.other_value.trim())
      ),
    [form]
  );

  const handleSubmit = async () => {
    setLoading(true);
    setError("");
    try {
      if (!tenantId) {
        throw new Error("Volvé al paso workspace para crear tu organización");
      }
      if (!form.name.trim() || form.name.trim().length < 2) {
        throw new Error("Nombre de contacto es obligatorio (mín. 2 caracteres)");
      }
      if (!form.email.trim() || !form.phone.trim()) {
        throw new Error("Email y teléfono son obligatorios");
      }

      const social = {};
      if (form.linkedin.trim()) social.linkedin = form.linkedin.trim();
      if (form.instagram.trim()) social.instagram = form.instagram.trim();
      if (form.website.trim()) social.website = form.website.trim();
      if (form.other_label.trim() && form.other_value.trim()) {
        social.other_label = form.other_label.trim();
        social.other_value = form.other_value.trim();
      }

      await updateTenantContact({
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        whatsapp: form.whatsapp,
        social: Object.keys(social).length ? social : undefined,
      });

      // SPEC-20 — enqueue HubSpot sync (non-blocking)
      syncTenantToHubSpot({
        contact_email: form.email.trim(),
        contact_phone: form.phone.trim(),
        contact_name: form.name.trim(),
      }).catch((err) => {
        console.warn("HubSpot sync enqueue failed (non-blocking):", err);
      });

      trackEvent("onboarding_step_3_contact_complete", {
        event_category: "onboarding",
        event_label: "contact_info_saved",
        step_number: 3,
        tenant_id: tenantId,
        user_email: form.email.trim(),
      });

      sessionStorage.setItem("onboarding_contact_done", "true");
      navigate("/onboarding/equipo");
    } catch (e) {
      setError(e.message || "No se pudieron guardar los datos de contacto");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="ob-card-header">
        <div className="ob-step-icon">
          <ContactMailOutlinedIcon sx={{ fontSize: 24 }} />
        </div>
        <div className="ob-card-header-text">
          <h2 className="ob-step-title">Datos de contacto</h2>
          <p className="ob-step-desc">
            Para coordinar la activación de tu cuenta y el soporte inicial.
          </p>
        </div>
      </div>

      <div className="ob-field">
        <label>Nombre del contacto *</label>
        <input
          type="text"
          placeholder="María García"
          value={form.name}
          onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          required
        />
      </div>

      <div className="ob-field">
        <label>Email de contacto *</label>
        <input
          type="email"
          placeholder="contacto@empresa.com"
          value={form.email}
          onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
          required
        />
        <p className="ob-hint">Puede ser distinto al email de tu cuenta.</p>
      </div>

      <div className="ob-field">
        <label>Teléfono / WhatsApp *</label>
        <input
          type="tel"
          placeholder="+54 11 5555-1234"
          value={form.phone}
          onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
          required
        />
      </div>

      <label className="ob-checkbox-row">
        <input
          type="checkbox"
          checked={form.whatsapp}
          onChange={(e) => setForm((f) => ({ ...f, whatsapp: e.target.checked }))}
        />
        <span>Este número es WhatsApp</span>
      </label>

      <button
        type="button"
        className="ob-social-toggle"
        onClick={() => setShowSocial((v) => !v)}
      >
        Redes sociales (opcional)
        {showSocial || socialFilled ? (
          <ExpandLessIcon sx={{ fontSize: 18 }} />
        ) : (
          <ExpandMoreIcon sx={{ fontSize: 18 }} />
        )}
      </button>

      {(showSocial || socialFilled) && (
        <div className="ob-social-fields">
          <div className="ob-field">
            <label>LinkedIn</label>
            <input
              placeholder="https://linkedin.com/company/..."
              value={form.linkedin}
              onChange={(e) => setForm((f) => ({ ...f, linkedin: e.target.value }))}
            />
          </div>
          <div className="ob-field">
            <label>Instagram</label>
            <input
              placeholder="@tuempresa"
              value={form.instagram}
              onChange={(e) => setForm((f) => ({ ...f, instagram: e.target.value }))}
            />
          </div>
          <div className="ob-field">
            <label>Sitio web</label>
            <input
              placeholder="https://tuempresa.com"
              value={form.website}
              onChange={(e) => setForm((f) => ({ ...f, website: e.target.value }))}
            />
          </div>
          <div className="ob-field-row">
            <div className="ob-field">
              <label>Otra red</label>
              <input
                placeholder="Ej. X / TikTok"
                value={form.other_label}
                onChange={(e) => setForm((f) => ({ ...f, other_label: e.target.value }))}
              />
            </div>
            <div className="ob-field">
              <label>Usuario o URL</label>
              <input
                placeholder="@cuenta"
                value={form.other_value}
                onChange={(e) => setForm((f) => ({ ...f, other_value: e.target.value }))}
              />
            </div>
          </div>
        </div>
      )}

      {error && <p className="ob-error">{error}</p>}

      <button className="ob-btn-primary" onClick={handleSubmit} disabled={loading} type="button">
        {loading ? "Guardando..." : "Continuar →"}
      </button>
    </>
  );
}
